import toast from 'react-hot-toast'
import { useLaunchPatrol, useMyFleet } from '@/api/hooks'
import { GoldButton } from '@/components/game/ui'

/**
 * Onboarding call-to-action shown on the dashboard until the captain has run
 * their (one-shot) reconnaissance patrol. Deliberately slim — one sentence + the
 * gold button — so the single heroic action isn't drowned in text. The loop
 * teaches itself afterwards (launch toast → live countdown → revealed objectives).
 * The crossing-event risk is NOT previewed here on purpose ("no fear before the
 * first pleasure") — it lives on the real launch surface (the map side panel).
 * The patrol itself is fast, safe (no events) and always pays out a wreck's gold.
 */
export function FirstPatrolCard() {
  const launch = useLaunchPatrol()
  // A docked ship is required — existing captains may have their whole fleet at sea.
  const { data: fleet } = useMyFleet()
  const hasDockedShip = (fleet?.ships ?? []).some((s) => s.status === 'docked')

  return (
    <section className="rounded-2xl border border-sand-300/40 bg-gradient-to-b from-sand-300/10 to-[#06222b]/45 p-6 sm:p-7 space-y-4">
      <div>
        <p className="text-[11px] uppercase tracking-[0.32em] text-sand-300 mb-2">Premier ordre du jour</p>
        <h2 className="font-display text-2xl sm:text-3xl text-sand-100">Lance ta première patrouille</h2>
      </div>

      <p className="text-sm text-ocean-100 leading-relaxed">
        Envoie un navire en reconnaissance autour de ton île : rapide, sans risque, toujours payant.
        Comme toute mission, elle avance sans toi : pas besoin de rester connecté.
      </p>

      <GoldButton
        onClick={() =>
          launch.mutate(undefined, {
            onSuccess: () => toast.success('⚓ Patrouille lancée ! Suis le compte à rebours ci-dessous.', { duration: 5000 }),
            onError: (e) => toast.error(e.message || 'Lancement impossible'),
          })
        }
        disabled={launch.isPending || !hasDockedShip}
        title={!hasDockedShip ? 'Aucun navire à quai' : undefined}
        className="w-full sm:w-auto px-6 py-2.5"
      >
        {launch.isPending ? 'Lancement…' : 'Lancer la patrouille de reconnaissance'}
      </GoldButton>

      {hasDockedShip ? (
        <p className="text-[11px] text-ocean-400">
          Tes expéditions (commerce, raid, exploration) se débloquent dès la patrouille terminée.
        </p>
      ) : (
        <p className="text-[11px] text-rum-300">
          Aucun navire à quai. Un navire doit être disponible pour patrouiller (attends le retour d'une mission).
        </p>
      )}
    </section>
  )
}
